import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { type QuizBankBuildPlace } from './tracking/events.quiz-bank';
import { QuizBankViewContainer } from './components';
import { QUIZ_BANK_MODAL_SIZE_CLASS } from './consts';
import { type QuizBankProblem } from './types';
interface QuizBankModalProps {
  /** 7자리 카테고리 트리 id. 초등 수학만 유효하다 */
  categoryTreeId: string;
  /** 빌드 폼 문항 상한 − 현재 문항 수 */
  maxSelectableCount: number;
  /** 이미 빌드에 담긴 은행 문항 id — 다시 담을 수 없게 잠근다 */
  addedProblemIds: string[];
  /** 은행을 연 표면. */
  place: QuizBankBuildPlace;
}

/**
 * 편집 중 재진입용 독립 모달. `QuizSetupModal` 의 BANK 단계와 같은 뷰·헤더를
 * 다른 셸에 얹은 것이라 닫기 버튼만 있고 뒤로가기는 없다.
 */
const QuizBankModal = ({
  categoryTreeId,
  maxSelectableCount,
  addedProblemIds,
  place,
  onAdd,
  onClose
}: QuizBankModalProps & {
  onAdd: (problems: QuizBankProblem[]) => void;
  onClose: () => void;
}) => {
  const {
    t
  } = useTranslation(['build']);
  const title = t('build:quizBank.title');
  return <ResponsiveDialog size="2xl" className={cn('h-[90%] max-h-[800px] tablet-sm:gap-xs', QUIZ_BANK_MODAL_SIZE_CLASS)} onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="QuizBankModal" data-sentry-source-file="index.tsx">
      <DialogHeader leftSlot={<div className="flex min-w-0 items-center gap-xs">
            <span className="truncate">{title}</span>
            <BetaChip className="shrink-0" />
          </div>} ariaTitle={title} hasCloseButton onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="index.tsx" />
      <QuizBankViewContainer place={place} categoryTreeId={categoryTreeId} maxSelectableCount={maxSelectableCount} addedProblemIds={addedProblemIds} onAdd={onAdd} data-sentry-element="QuizBankViewContainer" data-sentry-source-file="index.tsx" />
    </ResponsiveDialog>;
};
export const quizBankModal = {
  /** 담은 문항으로 resolve 한다. 담지 않고 닫으면 빈 배열이다. */
  openAsync(props: QuizBankModalProps) {
    return overlay.openAsync<QuizBankProblem[]>(({
      close,
      unmount
    }) => {
      const settle = (problems: QuizBankProblem[]) => {
        close(problems);
        unmount();
      };
      return <QuizBankModal {...props} onAdd={settle} onClose={() => settle([])} />;
    });
  }
};
export { QUIZ_BANK_MODAL_SIZE_CLASS } from './consts';
export { QuizBankEntryCard, QuizBankEntryImpression, QuizBankViewContainer } from './components';
export { type QuizBankEntry, useQuizBankEntry } from './hooks/useQuizBankEntry';
export { useShowQuizBankModal } from './hooks/useShowQuizBankModal';
export { isQuizBankSupported, toQuizBankCategoryTreeId } from './utils';
export type { QuizBankProblem } from './types';